<template>
  <div v-if="isViewReady" class="rentalObjectProspectDetails">
    <hl-Modal
      v-if="isCreditCheckModalShown"
      iconClass="budicon-performance-board-tick text-primary"
      title="Inviter til kredittsjekk"
    >
      <ValidationProvider
        name="vilkår for kredittsjekk"
        :rules="{ required: { allowFalse: false } }"
        v-slot="{ errors }"
      >
        <div>
          <p>Inviterer interessent(er) til din ledige bolig til å gjennomføre en kredittsjekk.</p>
          <p class="mb-5">
            Pris per kredittsjekk er
            <hl-Currency :amount="49" />
          </p>

          <div class="mt-3 mb-5">
            <div class="row ml-5 pl-2">
              <div class="col form-group form-check text-left">
                <input name="GDPR" v-model="isCreditCheckTermsAccepted" type="checkbox" />
                <span class="checkmark checkmark-sm"></span>
                <label class="ml-2">
                  Jeg aksepterer
                  <a
                    rel="noopener noreferrer"
                    href="/juridisk/vilkar-for-kredittsjekk-av-interessenter/"
                    target="_blank"
                  >
                    vilkår for kredittsjekk
                  </a>
                </label>
              </div>
            </div>
          </div>

          <span
            v-if="isCreditCheckModalLoading"
            class="spinner-grow spinner-grow-sm"
            role="status"
            aria-hidden="true"
          />
          <button
            v-else
            class="btn btn-primary mx-2"
            :disabled="!isCreditCheckTermsAccepted || isCreditCheckModalLoading"
            @click="inviteProspectToCreditCheck"
          >
            Inviter
          </button>
          <button class="btn btn-outline-secondary mx-2" @click="toggleCreditCheckModal">Avbryt</button>
        </div>
      </ValidationProvider>
    </hl-Modal>

    <hl-Modal
      v-if="isDeclineProspectModalShown"
      iconClass="budicon-sad-chat text-danger"
      title="Send avslag til valgt interessent"
      class="text-center"
    >
      <div>
        <p class="mb-5">
          Ved å klikke på “Avslag” sendes en e-post til {{ prospect.name }} hvor det informeres om at han/hun ikke er
          aktuell som leietaker for boligen.
        </p>

        <button class="btn btn-danger mx-2" @click="declineAndClose">Avslag</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleDeclineProspectModal">Avbryt</button>
      </div>
    </hl-Modal>

    <hl-Modal
      v-if="isViewingsModalShown"
      iconClass="budicon-view text-primary"
      title="Inviter til visning"
      class="text-center"
    >
      <div>
        <p>
          For å invitere {{ prospect.name }} til visning må du skrive inn et tidspunkt og eventuell info om visningen
          under.
        </p>

        <BulkProspectViewingForm @updated="newViewing = $event" />

        <hl-LoaderDots v-if="isViewingsModalLoading"></hl-LoaderDots>
        <button v-else class="btn btn-primary mx-2" @click="inviteProspectToViewing">Inviter</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleViewingsModal">Avbryt</button>
      </div>
    </hl-Modal>

    <hl-Modal
      v-if="isNewContractModalShown"
      iconClass="budicon-compose text-primary"
      title="Opprett leiekontrakt"
      class="text-center"
    >
      <div>
        <p>Opprett leiekontrakt for et leieforhold med {{ prospect.name }}.</p>
        <p class="mb-5">
          Vil du opprette leiekontrakten for et annet leieobjekt enn den annonserte boligen velger du et annet fra
          listen under.
        </p>

        <RentalObjectSelectMenu
          :rentalObjectId="rentalObjectId"
          :prospectId="prospectId"
          @input="actionRentalObjectId = $event"
        />

        <div class="mt-5">
          <a :href="createContractURL" class="btn btn-primary mx-2"> Opprett </a>
          <button class="btn btn-outline-secondary mx-2" @click="toggleNewContractModal">Avbryt</button>
        </div>
      </div>
    </hl-Modal>

    <div class="d-flex justify-content-between">
      <router-link :to="{ name: 'rentalObject_ads' }" class="d-flex mt-1">
        <i class="budicon-arrow-left h4" />
        <span class="ml-2 mt-1"> Tilbake til interessenter </span>
      </router-link>

      <div class="rentalObjectProspectDetails__buttonsDesktop mt-2">
        <hl-Button
          v-if="prospect.status !== 'rejected'"
          variant="danger"
          outline
          @click="toggleDeclineProspectModal(prospect)"
        >
          Avslag
        </hl-Button>

        <hl-Button outline @click="toggleViewingsModal(prospect)"> Inviter til visning </hl-Button>

        <hl-Button @click="toggleNewContractModal(prospect)"> Opprett leiekontrakt </hl-Button>
      </div>
    </div>

    <div class="d-flex">
      <CandidateAvatar
        :avatar-id="prospect.avatar_id"
        variant="circle"
        size="60"
        has-border
      />

      <div class="ml-3">
        <h2 class="m-0">{{ prospect.name }}</h2>
        <p>
          Interesse mottatt <hl-Date :date="prospect.created" />
        </p>
      </div>
    </div>

    <div class="d-flex justify-content-between align-items-end mt-2">
      <!-- extra div to shift the back / next div to the right if buttons are hidden -->
      <div>
        <div class="rentalObjectProspectDetails__buttonsMobile">
          <hl-Button
            v-if="prospect.status !== 'rejected'"
            variant="danger"
            outline
            @click="toggleDeclineProspectModal(prospect)"
          >
            Avslag
          </hl-Button>

          <hl-Button outline @click="toggleViewingsModal(prospect)"> Inviter til visning </hl-Button>
        </div>
      </div>

      <div class="d-flex">
        <a
          v-if="prevNextProspectId('previous')"
          class="d-flex align-items-center"
          :href="getProspectLink(prevNextProspectId('previous'))"
        >
          <i class="budicon-chevron-left" />
          <span class="ml-1"> Forrige </span>
        </a>

        <a
          v-if="prevNextProspectId('next')"
          class="d-flex align-items-center ml-3"
          :href="getProspectLink(prevNextProspectId('next'))"
        >
          Neste
          <i class="budicon-chevron-right ml-1" />
        </a>
      </div>
    </div>

    <div class="rentalObjectProspectDetails__grid mt-3">
      <BlockContainer class="p-4">
        <div class="d-flex">
          <h5>Din vurdering</h5>
          <span :title="ratingHelpText">
            <Icon :name="IconName.InterfaceInfo" class="ml-2" />
          </span>
        </div>

        <hl-RatingStars
          :value="prospect.rank"
          @newScore="
            updateProspectScore({
              landlordId: landlordId,
              prospectId: prospect.id,
              rank: $event,
              iMId: iMId,
            })
          "
        />

        <h5 class="mt-5">Beskrivelse</h5>

        <p class="text-gray2">
          {{ introText }}
        </p>

        <button
          v-if="isIntroLengthAboveMax"
          class="rentalObjectProspectDetails__grid__introButton"
          @click="isIntroExpanded = !isIntroExpanded"
        >
          {{ isIntroExpanded ? 'Vis mindre' : 'Les mer' }}
        </button>

        <template v-if="hasDetailsSection">
          <hr />

          <span v-if="prospect.gender" class="d-flex mb-2"> Kjønn: {{ formatGender(prospect.gender) }} </span>

          <span v-if="prospect.marital_status" class="d-flex mb-2">
            Sivilstatus: {{ formatMaritalStatus(prospect.marital_status) }}
          </span>

          <span v-if="prospect.with_kids" class="d-flex mb-2"> Med barn: Ja </span>

          <span v-if="prospect.pets" class="d-flex mb-2"> Kjæledyr: {{ formatPets(prospect.pets) }} </span>

          <span v-if="prospect.student_status" class="d-flex mb-2">
            Studentstatus: {{ formatStatus(prospect.student_status) }}
            <template v-if="prospect.education">({{ prospect.education }})</template>
          </span>

          <span v-if="prospect.work_status" class="d-flex mb-2">
            Arbeidsstatus: {{ formatStatus(prospect.work_status) }}
            <template v-if="prospect.workplace">({{ prospect.workplace }})</template>
          </span>

          <div v-if="prospect.application.numberOfTenants > 1" class="d-flex mb-2">
            + {{ prospect.application.numberOfTenants - 1 }} medleietakere
          </div>
        </template>

        <hr />

        <ProspectCreditCheck :prospect="prospect" :iMId="iMId" class="mt-1" />

        <hr />

        <div class="d-flex align-items-center mb-2">
          <i class="budicon-email h5 text-primary" />
          <span class="ml-3 mb-1">
            {{ prospect.email }}
          </span>
        </div>

        <div class="d-flex align-items-center mb-2">
          <i class="budicon-phone-block h5 text-primary" />
          <span class="ml-3 mb-1">
            {{ prospect.phone }}
          </span>
        </div>

        <div v-if="prospect.application.numberOfTenants > 1" class="d-flex align-items-center">
          <i class="budicon-user h5 text-primary" />
          <span class="ml-3 mb-1"> + {{ prospect.application.numberOfTenants - 1 }} medleietakere </span>
        </div>

        <hr />

        <h5>Referanser</h5>

        <template v-if="references.length">
          <div v-for="reference in references" :key="reference.id" class="d-flex align-items-center mt-4">
            <Icon :name="IconName.UserProfile" />
            <div class="ml-3">
              <h6>{{ reference.name }} ({{ reference.category === 'landlord' ? 'Utleier' : 'Arbeidsgiver' }})</h6>
              <a :href="`mailto:${reference.email}`" class="text-gray2 mb-0">
                <u>{{ reference.email }}</u>
              </a>
              <p class="text-gray2">
                {{ reference.phone }}
              </p>
            </div>
          </div>
        </template>

        <p v-else>Interessenten har ikke lagt til referanser</p>

        <hr />

        <h5>Notater</h5>

        <hl-Textarea
          class="mt-3"
          placeholder="Her kan du legge inn et internt notat om denne kandidaten. Det vil bare være synlig for deg."
          v-model="handleComment"
          :rows="5"
        />
      </BlockContainer>

      <BlockContainer class="p-4">
        <h5>Meldinger</h5>

        <RentalObjectProspectDetailsMessages :prospectId="prospectId" :landlordId="landlordId" :iMId="iMId" />
      </BlockContainer>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { debounce } from 'lodash';
import BlockContainer from '@/components/BlockContainer.vue';
import RatingStars from '@modules/husleie-framework/src/components/rating/RatingStars.vue';
import RentalObjectProspectDetailsMessages from '@/views/rentalObject/iM/details/RentalObjectProspectDetailsMessages.vue';
import HlDate from '@modules/husleie-framework/src/components/time/Date.vue';
import Icon, { IconName } from '@modules/design-system/components/Icon';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import ProspectCreditCheck from '@/views/rentalObject/iM/ProspectCreditCheck.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import BulkProspectViewingForm from '@/components/detailedRentalObject/interestManagement/forms/viewings/BulkProspectViewingForm';
import RentalObjectSelectMenu from '@/components/rentalObjects/RentalObjectSelectMenu';
import CandidateAvatar from '@/components/CandidateAvatar.vue';

export default Vue.extend({
  name: 'RentalObjectProspectDetails',
  data() {
    return {
      IconName,
      isIntroExpanded: false,
      comment: '',
      isViewReady: false,
      postNewCommentDebounced: undefined,
      isCreditCheckTermsAccepted: false,
      newViewing: {},
      actionRentalObjectId: undefined,
    };
  },
  props: {
    landlordId: {
      type: [Number, String],
      required: true,
    },
    rentalObjectId: {
      type: [Number, String],
      required: true,
    },
    landlordId: {
      type: [Number, String],
      required: true,
    },
    iMId: {
      type: String,
      required: true,
    },
    prospectId: {
      type: [Number, String],
      required: true,
    },
  },
  components: {
    BlockContainer,
    'hl-RatingStars': RatingStars,
    'hl-Date': HlDate,
    'hl-Button': Button,
    Icon,
    ProspectCreditCheck,
    RentalObjectProspectDetailsMessages,
    BulkProspectViewingForm,
    RentalObjectSelectMenu,
    CandidateAvatar,
  },
  MAX_INTRO_LENGTH: 380,
  computed: {
    ...mapGetters([
      'getCurrentIMId',
      'getProspectList',
      'getProspectDetails',
      'getEmployerRefferences',
      'getLandlordRefferences',
      'getProspectList',
      'isDeclineProspectModalShown',
      'isCreditCheckModalShown',
      'isCreditCheckModalLoading',
      'isViewingsModalLoading',
      'isViewingsModalShown',
      'isNewContractModalShown',
    ]),

    prospect() {
      return this.getProspectDetails(this.getCurrentIMId, this.prospectId);
    },
    isIntroLengthAboveMax() {
      return this.prospect.application.introduction?.length > this.$options.MAX_INTRO_LENGTH;
    },
    introText() {
      if (this.prospect.application.introduction && !this.isIntroExpanded) {
        return this.prospect.application.introduction.slice(0, this.$options.MAX_INTRO_LENGTH);
      } else if (this.prospect.application.introduction) {
        return this.prospect.application.introduction;
      } else {
        return 'Interessenten har ikke lagt inn beskrivelse';
      }
    },
    ratingHelpText() {
      return (
        'Stjernerangeringen gir deg som utleier en enkel og oversiktlig måte å evaluere leietakere ' +
        'på. Denne vurderingen er kun synlig for deg og er ment for intern bruk, slik at du ' +
        'lettere kan holde oversikt over tidligere og nåværende leietakere.'
      );
    },
    references() {
      return [
        ...this.getEmployerRefferences(this.iMId, this.prospectId),
        ...this.getLandlordRefferences(this.iMId, this.prospectId),
      ];
    },
    handleComment: {
      get() {
        return this.comment;
      },
      set(value) {
        this.comment = value;
        this.postNewCommentDebounced();
      },
    },
    createContractURL() {
      return RedirectOperations.getURL(AppRedirect.CONTRACT_CREATE, {
        rentalObjectId: this.actionRentalObjectId || this.rentalObjectId,
        candidateId: this.prospectId,
      });
    },
    hasDetailsSection() {
      return (
        this.prospect.application.numberOfTenants > 1 ||
        this.prospect.gender ||
        this.prospect.marital_status ||
        this.prospect.with_kids ||
        this.prospect.pets ||
        this.prospect.student_status ||
        this.prospect.work_status
      );
    }
  },
  methods: {
    ...mapMutations(['setCurrentIMId']),
    ...mapActions([
      'getLandlordInterestManagementCandidates',
      'toggleCreditCheckModal',
      'toggleNewContractModal',
      'toggleViewingsModal',
      'toggleDeclineProspectModal',
      'declineProspects',
      'updateProspectScore',
      'addNewComment',
      'getLandlordInterestManagementCandidates',
      'inviteToViewing',
      'inviteToCreditCheck',
    ]),

    formatGender(gender) {
      const genderMap = {
        male: 'Mann',
        female: 'Kvinne',
        other: 'Annet',
      };
      return genderMap[gender] || gender;
    },
    formatMaritalStatus(maritalStatus) {
      const maritalStatusMap = {
        single: 'Single',
        married: 'Gift',
        inPartnership: 'I et forhold',
        separatedOrDivorced: 'Separert eller skilt',
      };
      return maritalStatusMap[maritalStatus] || maritalStatus;
    },
    formatPets(pets) {
      if (!pets || !Array.isArray(pets) || pets.length === 0) {
        return null;
      }
      const petMap = {
        dog: 'Hund',
        cat: 'Katt',
        bird: 'Fugl',
        fish: 'Fisk',
        rodent: 'Gnager',
        other: 'Annet',
      };
      return pets.map(pet => petMap[pet] || pet).join(', ');
    },
    formatStatus(status) {
      const statusMap = {
        partTime: 'Deltid',
        fullTime: 'Fulltid',
      };
      return statusMap[status] || status;
    },

    prevNextProspectId(type) {
      const prospectIds = this.getProspectList(this.iMId)
        .map(item => item.id)
        .sort((a, b) => a - b);
      const index = prospectIds.indexOf(parseInt(this.prospectId));

      if (index === -1) {
        return undefined;
      } else if (type === 'previous') {
        return prospectIds[index - 1];
      } else if (type === 'next') {
        return prospectIds[index + 1];
      } else {
        return undefined;
      }
    },
    async postNewComment() {
      if (!this.comment) {
        return;
      }

      try {
        await this.addNewComment({
          prospectId: this.prospectId,
          comment: this.comment,
          landlordId: this.landlordId,
          iMId: this.iMId,
        });
      } catch (error) {
        this.$toast('Kunne ikke lagre notat.', 'error');
      }
    },
    getProspectLink(prospectId) {
      const { href } = this.$router.resolve({
        name: 'rentalObject_prospect_details',
        params: {
          landlordId: this.landlordId,
          rentalObjectId: this.rentalObjectId,
          iMId: this.iMId,
          prospectId,
        },
      });

      return href;
    },
    fetchCandidates() {
      this.getLandlordInterestManagementCandidates({
        landlordId: this.landlordId,
        iMId: this.iMId,
      });
    },
    async inviteProspectToCreditCheck() {
      try {
        await this.inviteToCreditCheck({
          landlordId: this.landlordId,
          iMId: this.iMId,
          prospectId: this.prospectId,
        });

        this.fetchCandidates();
        this.$toast('Invitasjon sendt.', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke sende invitasjon.', 'error');
      }
    },
    async declineAndClose() {
      try {
        await this.declineProspects({
          landlordId: this.landlordId,
          iMId: this.iMId,
          prospects: [this.prospectId],
        });

        this.fetchCandidates();
        this.$toast('Avslag sendt.', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke avslå interessent.', 'error');
      }
      this.toggleDeclineProspectModal();
    },
    async inviteProspectToViewing() {
      try {
        await this.inviteToViewing({
          landlordId: this.landlordId,
          iMId: this.iMId,
          prospectId: this.prospectId,
          viewing: this.newViewing,
        });

        this.fetchCandidates();
        this.$toast('Invitasjon sendt.', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke sende invitasjon.', 'error');
      }
    },
  },
  async created() {
    this.setCurrentIMId(this.iMId);
    await this.getLandlordInterestManagementCandidates({
      landlordId: this.landlordId,
      iMId: this.iMId,
    });

    const { comments } = this.prospect;

    this.comment = comments[comments.length - 1]?.comment;
    this.postNewCommentDebounced = debounce(this.postNewComment, 3000);
    this.isViewReady = true;
  },
});
</script>

<style lang="scss">
.rentalObjectProspectDetails {
  padding-right: 0rem;
}
.rentalObjectProspectDetails__candidateName {
  gap: 0 16px;
}
.rentalObjectProspectDetails__buttonsDesktop {
  display: none;
}
.rentalObjectProspectDetails__buttonsMobile {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem 0rem;
}
.rentalObjectProspectDetails__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem 0rem;
}
.rentalObjectProspectDetails__grid hr {
  background: #e9eaeb;
  width: 70%;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}
.rentalObjectProspectDetails__grid__introButton {
  border: none;
  background: none;
  padding: 0;
  text-decoration: underline;
}

@media (min-width: 1150px) {
  .rentalObjectProspectDetails {
    padding-right: 2.5rem;
  }
  .rentalObjectProspectDetails__grid {
    display: grid;
    grid-template-columns: 361px 1fr;
    gap: 0rem 1.5rem;
  }
  .rentalObjectProspectDetails__buttonsDesktop {
    display: flex;
    justify-content: flex-end;
    gap: 0rem 0.8rem;
  }
  .rentalObjectProspectDetails__buttonsMobile {
    display: none;
  }
}
</style>
